import React from "react";
import { PlayCircle } from "lucide-react";
import { motion } from "framer-motion";

// Mock YouTube Data: Replaced with farming/rice/powder related topics
const mockYouTubeData = [
  {
    title: "นโยบายข้าวไทย: สัมภาษณ์รัฐมนตรีเกษตรฯ",
    channel: "ThaiPBS",
    videoId: "dQw4w9WgXcQ", // Mock video ID
  },
  {
    title: "การผลิตแป้งข้าวแบบอินทรีย์ในพื้นที่ชนบท",
    channel: "Thairath",
    videoId: "TUVcZfQe-Kw", // Mock video ID
  },
  {
    title: "แผนรับมือภัยแล้ง: มุมมองจากนายกสมาคมชาวนา",
    channel: "VoiceTV",
    videoId: "nNWx9lwXlDU", // Mock video ID
  },
];

const slideUpVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const slideDownVariant = {
  hidden: { opacity: 0, y: -50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const NewsComponent = () => {
  return (
    <div className="min-h-screen h-full bg-white mt-28">
      {/* Top News Section */}
      <motion.div
        className="px-4 py-8 h-auto md:h-[730px] bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"
        initial="hidden"
        animate="visible"
        variants={slideUpVariant}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-6 gap-4 md:h-full">
            {/* Main News */}
            <motion.div
              className="col-span-6 md:col-span-3 bg-white/30 p-6 rounded text-white flex flex-col justify-between"
              variants={slideUpVariant}
              initial="hidden"
              animate="visible"
            >
              <div>
                <div className="mb-2 text-sm">Agriculture · 2 hours ago</div>
                <h1 className="text-[36px] md:text-[55px] font-bold mb-6 leading-tight">
                  รัฐบาลไทยประกาศลดภาษีเพื่อสนับสนุนการส่งออกข้าวและผลิตภัณฑ์แป้ง
                </h1>
                <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                  {[...Array(2)].map((_, index) => (
                    <motion.div
                      key={index}
                      className="w-full p-3 cursor-pointer bg-white/40 rounded hover:shadow-lg transition-shadow"
                      variants={slideUpVariant}
                      initial="hidden"
                      animate="visible"
                      whileHover={{ scale: 1.05 }}
                    >
                      <div className="w-24 h-16 bg-gray-200 mb-2.5"></div>
                      <p className="text-md">
                        ชาวนาไทยเริ่มใช้เทคโนโลยีใหม่ในการเพิ่มผลผลิตข้าวและลดต้นทุน
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Trending Section */}
            <motion.div
              className="col-span-6 md:col-span-3 flex justify-center rounded h-full"
              variants={slideDownVariant}
              initial="hidden"
              animate="visible"
            >
              <div className="bg-white/30 px-4 py-6 rounded-lg w-full max-w-[504px]">
                <h1 className="text-3xl mb-2.5 text-white">เทรนด์มาแรง</h1>
                <div className="space-y-4">
                  {[...Array(5)].map((_, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center space-x-4 bg-white/20 p-3 rounded hover:shadow-lg transition-shadow"
                      variants={slideUpVariant}
                      initial="hidden"
                      animate="visible"
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="w-24 h-16 bg-gray-200 rounded"></div>
                      <div className="flex-1">
                        <div className="text-xs mb-1">Farming · 5 hours ago</div>
                        <p className="text-sm">
                          การปลูกข้าวพันธุ์ใหม่ที่สามารถต้านทานโรคได้ดีกว่าเดิม
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Latest News Grid */}
      <motion.div
        className="px-4 py-6"
        initial="hidden"
        animate="visible"
        variants={slideUpVariant}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">ข่าวสารล่าสุดจากเรา</h2>
            <button className="bg-[#140FA4] p-2.5 w-[112px] rounded font-medium text-white hover:bg-blue-800 transition-colors">
              View All
            </button>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            {[...Array(8)].map((_, index) => (
              <motion.div
                key={index}
                className="group col-span-1 bg-white rounded-lg overflow-hidden border hover:shadow-lg transition-shadow"
                variants={slideUpVariant}
                initial="hidden"
                animate="visible"
                whileHover={{ scale: 1.03 }}
              >
                <div className="relative aspect-w-3 aspect-h-3 bg-[#909090] mb-3 w-full h-[300px]">
                  <div className="absolute left-0 bottom-0 flex justify-center items-center w-[76px] h-[30px] bg-white text-xs font-semibold">
                    Farming
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-medium mb-2 group-hover:text-blue-600">
                    โรงสีแป้งข้าวในภาคอีสานเริ่มขยายตลาดส่งออกไปยังยุโรป
                  </h3>
                  <div className="text-xs text-gray-500">1 hour ago</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Videos Section (Default) */}
      <motion.div
        className="px-4 py-6 bg-gray-50"
        initial="hidden"
        animate="visible"
        variants={slideUpVariant}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Videos</h2>
            <button className="bg-[#140FA4] p-2.5 w-[112px] rounded font-medium text-white hover:bg-blue-800 transition-colors">
              View All
            </button>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Featured Video */}
            <motion.div
              className="relative bg-white rounded-lg shadow p-3"
              variants={slideUpVariant}
              initial="hidden"
              animate="visible"
              whileHover={{ scale: 1.02 }}
            >
              <div className="aspect-w-16 aspect-h-9 bg-[#909090] rounded-lg w-full h-[378px] md:h-full relative overflow-hidden">
                <div className="absolute left-0 bottom-0 flex justify-center items-center w-[76px] h-[30px] bg-white text-sm font-semibold">
                  Rice
                </div>
                <PlayCircle className="absolute inset-0 m-auto w-16 h-16 text-white cursor-pointer hover:scale-110 transition-transform" />
              </div>
              <h3 className="mt-3 text-sm font-medium">
                เทคนิคการปลูกข้าวอินทรีย์ที่ช่วยลดสารเคมีและเพิ่มคุณภาพผลผลิต
              </h3>
              <div className="text-xs text-gray-500 mt-1">3 hours ago</div>
            </motion.div>

            {/* Additional Videos */}
            <motion.div
              className="space-y-4"
              variants={slideUpVariant}
              initial="hidden"
              animate="visible"
            >
              {[
                "กระบวนการผลิตแป้งข้าวที่เป็นมิตรต่อสิ่งแวดล้อม",
                "เครื่องจักรช่วยเกษตรกรลดเวลาการเก็บเกี่ยวข้าว",
                "ภูมิปัญญาท้องถิ่น: แป้งข้าวเหนียวคุณภาพสูง",
              ].map((title, index) => (
                <motion.div
                  key={index}
                  className="flex space-x-4 bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow"
                  variants={slideUpVariant}
                  initial="hidden"
                  animate="visible"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="relative w-40 h-24 bg-[#909090] rounded-lg flex-shrink-0 overflow-hidden">
                    <PlayCircle className="absolute inset-0 m-auto w-8 h-8 text-white cursor-pointer hover:scale-110 transition-transform" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium mb-1">{title}</h3>
                    <div className="text-xs text-gray-500">3 hours ago</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Mock YouTube Thai News Section */}
      <motion.div
        className="px-4 py-8 bg-white"
        initial="hidden"
        animate="visible"
        variants={slideUpVariant}
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl font-bold mb-6">YouTube Thai News (Mock)</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {mockYouTubeData.map((video, index) => (
              <motion.div
                key={index}
                className="bg-gray-100 rounded-lg overflow-hidden shadow hover:shadow-lg transition-shadow"
                variants={slideUpVariant}
                initial="hidden"
                animate="visible"
                whileHover={{ scale: 1.02 }}
              >
                <div className="aspect-w-16 aspect-h-9 bg-gray-200 relative">
                  {/* Mocked iframe embed */}
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${video.videoId}?controls=1`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-medium mb-1">{video.title}</h3>
                  <p className="text-xs text-gray-500">{video.channel}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Recommended Section */}
      <motion.div
        className="px-4 py-6 bg-gray-50"
        initial="hidden"
        animate="visible"
        variants={slideUpVariant}
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl font-bold mb-6">แนะนำสำหรับคุณ</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[...Array(3)].map((_, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-lg p-4 border hover:shadow-lg transition-shadow"
                variants={slideUpVariant}
                initial="hidden"
                animate="visible"
                whileHover={{ scale: 1.02 }}
              >
                <div className="relative aspect-w-3 aspect-h-3 bg-[#909090] mb-3 w-full h-[300px] rounded overflow-hidden">
                  <div className="absolute left-0 bottom-0 flex justify-center items-center w-[76px] h-[30px] bg-white text-xs font-semibold">
                    Rice
                  </div>
                </div>
                <h3 className="text-sm font-medium">
                  เคล็ดลับเพิ่มมูลค่าสินค้าการเกษตร ด้วยแป้งข้าวคุณภาพสูง
                </h3>
                <div className="text-xs text-gray-500 mt-2">2 hours ago</div>
              </motion.div>
            ))}
          </div>
          <div className="flex justify-center mt-8 space-x-2">
            <motion.button
              className="w-8 h-8 bg-blue-700 text-white rounded-lg"
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            >
              1
            </motion.button>
            {[2, 3, 4, 5].map((num) => (
              <motion.button
                key={num}
                className="w-8 h-8 text-gray-600 rounded-lg hover:bg-gray-200"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                {num}
              </motion.button>
            ))}
            <motion.button
              className="w-8 h-8 text-gray-600 rounded-lg hover:bg-gray-200"
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            >
              ›
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Footer (Optional) */}
      {/* You can add a footer here with similar motion effects if desired */}
    </div>
  );
};

export default NewsComponent;
